import { Phone, MessageCircle, Mail, Instagram, Facebook, Linkedin, Youtube } from "lucide-react";
import { Reveal } from "./Reveal";
import { LeadForm } from "./LeadForm";
import { useSiteSettings } from "@/hooks/useSiteSettings";

const socials = [
  { key: "instagram", Icon: Instagram },
  { key: "facebook", Icon: Facebook },
  { key: "linkedin", Icon: Linkedin },
  { key: "youtube", Icon: Youtube },
];

export const Contact = () => {
  const site = useSiteSettings();
  const activeSocials = socials.filter(({ key }) => site[key]);

  return (
    <section id="contact" className="bg-stone py-20 md:py-28" data-testid="contact-section">
      <div className="mx-auto max-w-7xl px-6 md:px-10">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <Reveal>
              <div data-testid="contact-info-panel">
                <h2 className="mt-5 font-display text-3xl font-extrabold uppercase leading-[1.1] tracking-tight text-navy sm:text-4xl" data-testid="contact-headline">
                  Get in touch with our team.
                </h2>
                <p className="mt-5 text-sm leading-relaxed text-steel">
                  Land or no land. A clear budget or a rough number. Either way,
                  you get a straight read on the opportunity — usually the same day.
                </p>
                <a
                  href={`tel:${site.phone.replace(/\s/g, "")}`}
                  data-testid="contact-phone-link"
                  className="mt-8 flex items-center gap-3 font-display text-xl font-extrabold tracking-tight text-navy transition-colors duration-300 hover:text-amberdark"
                >
                  <Phone size={19} className="text-amberdark" />
                  {site.phone}
                </a>
                <div className="mt-4 flex flex-wrap items-center gap-3">
                  <a
                    href={`https://wa.me/${site.whatsapp}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-whatsapp-link"
                    className="inline-flex items-center gap-2 rounded-full border border-border bg-paper px-4 py-2 text-sm font-bold text-navy transition-colors duration-300 hover:border-amber hover:text-amberdark"
                  >
                    <MessageCircle size={15} />
                    WhatsApp
                  </a>
                  <a
                    href={`mailto:${site.email}`}
                    data-testid="contact-email-link"
                    className="inline-flex items-center gap-2 rounded-full border border-border bg-paper px-4 py-2 text-sm font-bold text-navy transition-colors duration-300 hover:border-amber hover:text-amberdark"
                  >
                    <Mail size={15} />
                    {site.email}
                  </a>
                </div>
                {activeSocials.length > 0 && (
                  <div className="mt-5 flex items-center gap-3" data-testid="contact-socials">
                    {activeSocials.map(({ key, Icon }) => (
                      <a
                        key={key}
                        href={site[key]}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`Urban Veera on ${key}`}
                        data-testid={`contact-social-${key}`}
                        className="flex h-9 w-9 items-center justify-center rounded-full border border-border text-steel transition-colors duration-300 hover:border-amber hover:text-amberdark"
                      >
                        <Icon size={15} />
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-8" id="contact-form" data-testid="contact-form">
            <Reveal delay={0.15}>
              <div className="rounded-2xl border border-border bg-paper p-6 shadow-soft md:p-10">
                <LeadForm idPrefix="lead" />
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
